// =============================================================================
// screens/DiscoverServersScreen.tsx — Discover Servers (ServersDiscover route).
//
// MIGRATION (bare RN, P3 wave-2 MISC SOCIAL): ported off apps/cryb-native
// app/servers/discover.tsx. Swaps: expo-router → react-navigation (useRouter →
// useNavigation; router.push('/servers/:id') → navigation.navigate('ServerDetail',
// { id }); Stack.Screen header registered in App.tsx); @expo/vector-icons → Icon
// shim. Services/theme/BackgroundBlobs paths unchanged. API contracts preserved.
//
// Lists public servers from GET /servers/discover ({ data: { servers, ... } }) and
// joins via POST /servers/:serverId/join (400 'Already a member' = already joined).
//
// DIRECTORY REDESIGN (triage P1s, reference IMG_5876/5873):
//  - Token-branded identity: icon-less servers get a deterministic lavender
//    gradient monogram (components/discover gradientForKey) instead of a flat
//    grey circle.
//  - Preview modal: tapping a NON-joined server opens the compact
//    CommunityPreviewModal bottom sheet (Join = POST /servers/:id/join, View =
//    ServerDetail). Joined servers navigate straight to ServerDetail.
//  - List bottom padding now clears the floating glass tab bar
//    (layout.bottomNavInset) — the last card was hidden behind it.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale, shadowTokens,
} from '../constants/theme';
import { api } from '../services/api';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import { GlassCard, GlassButton } from '../components/glass';
import { CommunityPreviewModal, gradientForKey } from '../components/discover';
import type { PreviewTarget } from '../components/discover';

type Nav = NativeStackNavigationProp<RootStackParamList>;

interface DiscoverServer {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  category?: string;
  memberCount: number;
  onlineCount: number;
  verified?: boolean;
  featured?: boolean;
}

// Parse one raw server defensively — every field may be missing/null.
function normalizeServer(raw: any): DiscoverServer {
  return {
    id: raw?.id ?? '',
    name: raw?.name ?? 'Server',
    description: typeof raw?.description === 'string' ? raw.description : undefined,
    icon: typeof raw?.icon === 'string' && raw.icon ? raw.icon : undefined,
    category: typeof raw?.category === 'string' ? raw.category : undefined,
    memberCount: typeof raw?.memberCount === 'number' ? raw.memberCount : 0,
    onlineCount: typeof raw?.onlineCount === 'number' ? raw.onlineCount : 0,
    verified: !!raw?.verified,
    featured: !!raw?.featured,
  };
}

export default function DiscoverServersScreen() {
  const navigation = useNavigation<Nav>();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const contentMaxWidth = layout === 'desktop' ? 900 : layout === 'tablet' ? 760 : undefined;
  const containerStyle = layout !== 'phone'
    ? { maxWidth: contentMaxWidth, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  // Responsive directory grid: 1 col phone, 2 tablet, 3 desktop.
  const gridColumns = layout === 'desktop' ? 3 : layout === 'tablet' ? 2 : 1;
  const [servers, setServers] = useState<DiscoverServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-server join progress + joined state (ids), so a slow request can't be
  // double-fired and a successful join flips the button to "Joined".
  const [joiningIds, setJoiningIds] = useState<Set<string>>(new Set());
  const [joinedIds, setJoinedIds] = useState<Set<string>>(new Set());
  // Preview modal (IMG_5873): tap a NON-joined server → compact join sheet.
  const [preview, setPreview] = useState<DiscoverServer | null>(null);

  const fetchServers = useCallback(async () => {
    setError(null);
    const res = await api<{ servers?: any[] }>('/servers/discover');
    if (res.success && res.data) {
      const raw = Array.isArray(res.data.servers) ? res.data.servers : [];
      setServers(raw.map(normalizeServer).filter((s) => !!s.id));
    } else {
      setError(res.error || 'Failed to load servers');
    }
    setLoading(false);
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchServers();
    setRefreshing(false);
  }, [fetchServers]);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  async function handleJoin(server: DiscoverServer, opts?: { silent?: boolean }) {
    if (!server.id || joiningIds.has(server.id) || joinedIds.has(server.id)) return;
    setJoiningIds((prev) => new Set(prev).add(server.id));
    const res = await api(`/servers/${encodeURIComponent(server.id)}/join`, { method: 'POST' });
    setJoiningIds((prev) => {
      const next = new Set(prev);
      next.delete(server.id);
      return next;
    });
    // A 400 "Already a member" means the user IS in the server — show Joined.
    const alreadyMember = !res.success && /already a member/i.test(res.error || '');
    if (res.success || alreadyMember) {
      setJoinedIds((prev) => new Set(prev).add(server.id));
      if (res.success) {
        setServers((prev) =>
          (Array.isArray(prev) ? prev : []).map((s) =>
            s.id === server.id ? { ...s, memberCount: (s.memberCount ?? 0) + 1 } : s
          )
        );
        // The preview modal already reflects the joined state visually — only
        // pop the celebratory alert for inline (card button) joins.
        if (!opts?.silent) Alert.alert('Joined', `Welcome to ${server.name}!`);
      }
    } else {
      Alert.alert('Error', res.error || 'Failed to join server. Please try again.');
    }
  }

  // --- Preview modal wiring ----------------------------------------------------
  function handleCardPress(server: DiscoverServer) {
    if (joinedIds.has(server.id)) {
      navigation.navigate('ServerDetail', { id: server.id });
    } else {
      setPreview(server);
    }
  }

  const previewTarget: PreviewTarget | null = preview ? {
    key: preview.id,
    title: preview.name,
    handle: preview.category ? preview.category : undefined,
    description: preview.description,
    avatar: preview.icon,
    memberCount: preview.memberCount ?? 0,
    joined: joinedIds.has(preview.id),
  } : null;

  function renderServerCard({ item }: { item: DiscoverServer }) {
    const joined = joinedIds.has(item.id);
    const joining = joiningIds.has(item.id);
    return (
      <View style={gridColumns > 1 ? styles.gridCell : styles.cardWrap}>
        <GlassCard
          padding={spacing.lg}
          radius={radius.xl}
          style={styles.card}
          contentStyle={styles.cardContent}
          onPress={() => handleCardPress(item)}
          accessibilityLabel={joined ? `Open ${item.name}` : `Preview ${item.name}`}
        >
          <View style={styles.cardRow}>
            {item.icon ? (
              <CachedImage source={{ uri: item.icon }} style={styles.avatar} />
            ) : (
              // Token-branded monogram: deterministic lavender-family gradient
              // per server (no more flat grey identity — triage P1).
              <LinearGradient
                colors={[...gradientForKey(item.name || item.id)]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.avatar, styles.avatarFallback]}
              >
                <Text style={styles.avatarLetter}>
                  {((item.name || 'S').charAt(0) || 'S').toUpperCase()}
                </Text>
              </LinearGradient>
            )}
            <View style={styles.cardInfo}>
              <View style={styles.nameRow}>
                <Text style={styles.cardName} numberOfLines={1}>{item.name}</Text>
                {item.verified ? (
                  <Ionicons name="checkmark-circle" size={14} color={colors.primary} />
                ) : null}
              </View>
              <View style={styles.memberRow}>
                <Ionicons name="people-outline" size={13} color={colors.muted} />
                <Text style={styles.memberText}>
                  {(item.memberCount ?? 0).toLocaleString()} members
                  {(item.onlineCount ?? 0) > 0 ? ` · ${(item.onlineCount ?? 0).toLocaleString()} online` : ''}
                </Text>
              </View>
            </View>
          </View>
          {item.description ? (
            <Text style={styles.cardDesc} numberOfLines={2}>{item.description}</Text>
          ) : null}
          <TouchableOpacity
            style={[styles.joinBtn, joined && styles.joinedBtn]}
            onPress={() => handleJoin(item)}
            disabled={joined || joining}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={joined ? `Joined ${item.name}` : `Join ${item.name}`}
            accessibilityState={{ disabled: joined || joining }}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            {joining ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <>
                <Ionicons
                  name={joined ? 'checkmark' : 'add'}
                  size={15}
                  color={joined ? colors.primary : colors.white}
                />
                <Text style={[styles.joinBtnText, joined && styles.joinedBtnText]}>
                  {joined ? 'Joined' : 'Join'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </GlassCard>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Living animated background (design-system): deep premium glass surface. */}
      <ParticleBackground />

      {loading ? (
        <View style={[styles.listContent, containerStyle]}>
          {[0, 1, 2, 3].map((i) => (
            <GlassCard key={i} style={styles.skeletonCard} padding={spacing.lg} radius={radius.xl}>
              <View style={styles.skeletonRow}>
                <View style={styles.skeletonAvatar} />
                <View style={styles.skeletonLines}>
                  <View style={[styles.skeletonLine, { width: '55%' }]} />
                  <View style={[styles.skeletonLine, styles.skeletonLineThin, { width: '38%' }]} />
                </View>
              </View>
            </GlassCard>
          ))}
        </View>
      ) : error && servers.length === 0 ? (
        <View style={styles.emptyWrap}>
          <GlassCard style={styles.emptyCard} padding={spacing.xl} radius={radius.xl}>
            <Asset3D name="error" size={110} style={{ marginBottom: spacing.md }} />
            <Text style={styles.emptyText}>Couldn't load servers</Text>
            <Text style={styles.emptySubtext}>{error}</Text>
            <GlassButton
              title="Retry"
              icon="refresh-outline"
              onPress={onRefresh}
              fullWidth
              style={styles.emptyCta}
            />
          </GlassCard>
        </View>
      ) : (
        <FlatList
          data={Array.isArray(servers) ? servers : []}
          keyExtractor={(item, index) => item?.id || String(index)}
          renderItem={renderServerCard}
          numColumns={gridColumns}
          key={`cols-${gridColumns}`}
          columnWrapperStyle={gridColumns > 1 ? styles.gridRow : undefined}
          ListHeaderComponent={
            servers.length > 0 ? (
              <Text style={styles.resultsLabel}>Discover  ·  {servers.length}</Text>
            ) : null
          }
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <GlassCard style={styles.emptyCard} padding={spacing.xl} radius={radius.xl}>
                <Asset3D name="community" size={110} style={{ marginBottom: spacing.md }} />
                <Text style={styles.emptyText}>No servers to discover</Text>
                <Text style={styles.emptySubtext}>Public servers will show up here as communities open their doors.</Text>
              </GlassCard>
            </View>
          }
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, containerStyle]}
        />
      )}

      {/* Server preview sheet (IMG_5873 pattern): join or peek before entering */}
      <CommunityPreviewModal
        visible={!!preview}
        target={previewTarget}
        joining={preview ? joiningIds.has(preview.id) : false}
        onClose={() => setPreview(null)}
        onJoin={() => { if (preview) handleJoin(preview, { silent: true }); }}
        onView={() => {
          if (!preview) return;
          const id = preview.id;
          setPreview(null);
          navigation.navigate('ServerDetail', { id });
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  // Card = shared GlassCard (real glass depth via GlassSurface). This style only
  // stretches it and separates it from siblings.
  cardWrap: {
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.md,
  },
  card: {
    flex: 1,
  },
  cardContent: {
    gap: spacing.md,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: radius.full,
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarLetter: {
    ...typographyScale.h3,
    color: colors.white,
  },
  cardInfo: {
    flex: 1,
    marginLeft: spacing.md,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  cardName: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
    flexShrink: 1,
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  memberText: {
    ...typographyScale.caption,
    color: colors.muted,
  },
  cardDesc: {
    ...typographyScale.body,
    fontSize: 13,
    lineHeight: 19,
    color: colors.ink2,
  },
  joinBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    minHeight: 40,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    ...shadowTokens.bar,
    shadowColor: colors.primary,
  },
  joinedBtn: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.primary,
    shadowOpacity: 0,
    elevation: 0,
  },
  joinBtnText: {
    ...typographyScale.caption,
    ...fonts.semibold,
    fontSize: 13,
    color: colors.white,
  },
  joinedBtnText: {
    color: colors.primary,
  },
  // Multi-column grid (tablet/desktop): the row owns the gutter + inter-card gap.
  gridRow: {
    paddingHorizontal: layoutTokens.gutter,
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  gridCell: {
    flex: 1,
  },
  resultsLabel: {
    ...typographyScale.caption,
    ...fonts.bold,
    color: colors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.md,
  },
  listContent: {
    paddingTop: spacing.md,
    // Clear the floating glass tab bar so the last server card is visible.
    paddingBottom: layoutTokens.bottomNavInset,
    flexGrow: 1,
  },
  // --- Loading skeleton ---
  skeletonCard: {
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.md,
  },
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  skeletonAvatar: {
    width: 48,
    height: 48,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '14',
  },
  skeletonLines: {
    flex: 1,
    gap: spacing.sm,
  },
  skeletonLine: {
    height: 12,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '14',
  },
  skeletonLineThin: {
    height: 10,
    backgroundColor: colors.primary + '0D',
  },
  // --- Empty / error states ---
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: spacing.xxxl + spacing.xxl,
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 64,
    height: 64,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  emptyCta: {
    marginTop: spacing.lg,
  },
});
